<template>
  <footer class="bg-stone-900 sm:pb-6">
    <!-- Desktop Footer -->
    <div
      class="container mx-auto px-4 max-w-screen-xl py-16 flex flex-col justify-between md:flex-row"
    >
      <!-- Logo and Description Section -->
      <div class="mb-10 md:mb-0 md:w-1/3">
        <div class="flex flex-col">
          <!-- Logo -->
          <div class="mb-5">
            <img src="@/assets/icons/logo_big.svg" alt="Logo" class="w-10 h-10" />
          </div>
          <!-- Description -->
          <p class="text-white text-sm leading-normal max-w-md mb-6 max-w-[310px]">
            {{ $t('footer.description') }}
          </p>
        </div>
      </div>

      <!-- Navigation Links -->
      <div class="grid grid-cols-2 sm:gap-8 md:gap-16">
        <!-- Information Column -->
        <div>
          <h3 class="text-white text-sm font-medium mb-2">{{ $t('footer.information') }}</h3>
          <ul class="space-y-2">
            <li v-for="(link, index) in informationLinks" :key="index">
              <router-link
                :to="link.to"
                class="text-white opacity-60 text-sm hover:text-white hover:opacity-100 hover:translate-x-0.5 hover:underline transition-all duration-300 ease-in-out inline-block font-light"
              >
                {{ $t(`footer.links.${link.key}`) }}
              </router-link>
            </li>
          </ul>
        </div>

        <!-- Pool Page Column -->
        <div>
          <h3 class="text-white text-sm font-medium mb-2">{{ $t('footer.poolPage') }}</h3>
          <ul class="space-y-2">
            <li v-for="(link, index) in poolLinks" :key="index">
              <router-link
                :to="link.to"
                class="text-white opacity-60 text-sm hover:text-white hover:opacity-100 hover:translate-x-0.5 hover:underline transition-all duration-300 ease-in-out inline-block font-light"
              >
                {{ $t(`footer.links.${link.key}`) }}
              </router-link>
            </li>
          </ul>
        </div>
      </div>
      <!-- Social Icons - Desktop -->
      <div class="flex space-x-4 mt-16 sm:mt-0">
        <template v-for="(item, index) in lucideSocialLinks" :key="index">
          <!-- Lucide Icons -->
          <a
            :href="item.url"
            class="text-white hover:text-gray-300 hover:scale-110 hover:-translate-y-0.5 transition-all duration-300 ease-in-out font-light"
          >
            <component :is="item.icon" class="w-5 h-5" />
          </a>
        </template>
        <template v-for="(item, index) in svgSocialLinks" :key="index">
          <!-- SVG Icons -->
          <a
            :href="item.url"
            class="text-white hover:text-gray-300 hover:scale-110 hover:-translate-y-0.5 transition-all duration-300 ease-in-out font-light"
          >
            <img :src="item.icon" :alt="item.name" class="w-5 h-5 filter brightness-0 invert" />
          </a>
        </template>
      </div>
      <!-- Bottom Links & Copyright -->
      <div class="sm:hidden container mx-auto max-w-screen-xl">
        <div class="flex flex-col items-start">
          <!-- Legal Links -->
          <div class="flex flex-wrap justify-center space-x-6 mt-8">
            <router-link
              v-for="(link, index) in legalLinks"
              :key="index"
              :to="link.to"
              class="text-white opacity-60 hover:text-white hover:opacity-100 hover:underline text-xs font-light transition-all duration-300 ease-in-out"
            >
              {{ $t(`footer.links.${link.key}`) }}
            </router-link>
          </div>
          <!-- Copyright -->
          <div class="text-white opacity-60 text-xs font-light mt-8 md:mb-0 text-center md:text-left">
            {{ $t('footer.copyright', { year: new Date().getFullYear() }) }}
          </div>
        </div>
      </div>
    </div>
    <!-- Bottom Links & Copyright -->
    <div class="hidden md:block container mx-auto max-w-screen-xl px-4">
      <div
        class="flex flex-col items-start flex-row md:flex-row-reverse md:justify-between md:items-center"
      >
        <!-- Legal Links -->
        <div class="flex flex-wrap justify-center md:justify-end space-x-6 mt-8">
          <router-link
            v-for="(link, index) in legalLinks"
            :key="index"
            :to="link.to"
            class="text-white opacity-60 hover:text-white hover:opacity-100 hover:underline text-xs transition-all duration-300 ease-in-out"
          >
            {{ $t(`footer.links.${link.key}`) }}
          </router-link>
        </div>
        <!-- Copyright -->
        <div class="text-white opacity-60 text-xs mt-8 md:mb-0 text-center md:text-left">
          {{ $t('footer.copyright', { year: new Date().getFullYear() }) }}
        </div>
      </div>
    </div>
  </footer>
</template>

<script setup lang="ts">
import TelegramIconSrc from '@/assets/icons/telegram.svg'
import XIconSrc from '@/assets/icons/x.svg'
import { Instagram, Linkedin, Youtube } from 'lucide-vue-next'

const informationLinks = [
  { to: '/', key: 'homePage' },
  { to: '/documentation', key: 'documentation' },
  { to: '/payout-rules', key: 'payoutRules' },
  { to: '/contacts', key: 'contacts' }
]

const poolLinks = [
  { to: '/pool', key: 'pool' },
  { to: '/next-block', key: 'nextBlock' },
  { to: '/my-statistics', key: 'myStatistics' },
  { to: '/payout', key: 'payout' }
]

const lucideSocialLinks = [
  { icon: Linkedin, url: '#', name: 'LinkedIn' },
  { icon: Youtube, url: '#', name: 'YouTube' },
  { icon: Instagram, url: '#', name: 'Instagram' }
]

const svgSocialLinks = [
  { icon: TelegramIconSrc, url: '#', name: 'Telegram' },
  { icon: XIconSrc, url: '#', name: 'X (Twitter)' }
]

const legalLinks = [
  { to: '/privacy-policy', key: 'privacyPolicy' },
  { to: '/terms-of-use', key: 'termsOfUse' },
  { to: '/legal', key: 'legal' }
]
</script>
